import React, { createContext, FC, useCallback, useEffect } from "react";

import { datadogRum } from "@datadog/browser-rum";

import { useResource } from "@natera/platform/lib/hooks";
import { ConfigService } from "@natera/platform/lib/service/config";
import { LoadingContext } from "@natera/platform/lib/components/context";
import defaultConfig from "@etc/config.json";
import defaultOktaConfig from "@etc/okta.json";
import defaultHeapConfig from "@etc/heap.json";
import defaultRecaptchaConfig from "@etc/recaptcha.json";
import defaultMaintenanceConfig from "@etc/maintenance.json";
import { initializeDatadogRum } from "@app/utils/ddRumInitializer";
import {
  ClientLogsConfig,
  configureClientLogger,
  LogLevel,
} from "@app/utils/logMsg";

const wrapHeapMethodSafely = (
  methodName: "track" | "identify" | "resetIdentity"
): void => {
  if (typeof heap === "undefined" || typeof heap[methodName] !== "function") {
    return;
  }

  const original = heap[methodName] as (...args: unknown[]) => unknown;
  if ((original as { __safeWrapped?: boolean }).__safeWrapped) {
    return;
  }

  const safeMethod = (...args: unknown[]) => {
    try {
      return original(...args);
    } catch {
      return undefined;
    }
  };
  (safeMethod as { __safeWrapped?: boolean }).__safeWrapped = true;
  (heap as Record<string, (...args: unknown[]) => unknown>)[methodName] =
    safeMethod;
};

const wrapHeapSafely = (): void => {
  wrapHeapMethodSafely("track");
  wrapHeapMethodSafely("identify");
  wrapHeapMethodSafely("resetIdentity");
};

export type OktaConfig = typeof defaultOktaConfig;
export type HeapConfig = typeof defaultHeapConfig;
export type ProsperaOrgans = {
  KIDNEY: string;
  HEART: string;
  SPK: string;
  LUNG: string;
  LIVER: string;
  OTHER: string;
};

export interface ProductLinks {
  RENASIGHT: string;
  VISTARA: string;
  EMPOWER: {
    EN: string;
    ES: string;
  };
  EMPOWER_ONCOLOGY: string;
  SIGNATERA: string;
  ALTERA: string;
  DPYD: string;
  HORIZON: string;
  PANORAMA: string;
  WOMENS_HEALTH: string;
  PROSPERA: ProsperaOrgans;
  LATITUDE: string;
}

export type EducationEntry = {
  title: string;
  link: string;
};

export interface EducationLinks {
  SIGNATERA?: EducationEntry[];
  ALTERA?: EducationEntry[];
  LATITUDE?: EducationEntry[];
  [key: string]: EducationEntry[] | undefined;
}

export type ClientConfig = {
  timers?: {
    idleTimer?: {
      timeoutDelay?: number;
      openedDialogDelay?: number;
    };
  };
  timeouts?: {
    resendTimeout?: number;
  };
  link: {
    product: ProductLinks;
    PATIENT_PORTAL_LINK: string;
    SCHEDULE_SESSION: string;
    CONTACT_US: string;
    COMPASSIONATE_CARE: string;
    TRACKING: string;
    TERMS: string;
    PRIVACY_POLICY: string;
    LICENSES: string;
    NOTICE_OF_PRIVACY_PRACTICES: string;
    WOMENS_HEALTH_PRICING_BILLING: string;
    EDUCATION: EducationLinks;
  };
  videoHashId: {
    SALIVA: {
      ES: string;
      EN: string;
    };
    HORIZON: {
      ES: string;
      EN: string;
    };
    PANORAMA: {
      ES: string;
      EN: string;
    };
  };
  wistia: {
    enabled: boolean;
    captionsEnabled: boolean;
  };
};

export type NevaConfig = {
  enabled: boolean;
  isAutoExpandEnabled: boolean;
  apiVersion: string;
  timeout: number;
  widgetLogsEnabled: boolean;
  horizonCS3Enabled: boolean;
  horizonSOPLEnabled: boolean;
  horizonXEnabled: boolean;
  panoramaEnabled: boolean;
  empowerEnabled: boolean;
};

export type PippaConfig = {
  sessionTtlMinutes?: number;
};

export type DDRumConfig = {
  applicationId: string;
  clientToken: string;
  env: string;
  version?: string;
  sessionSampleRate?: number;
  traceSampleRate?: number;
};

type RedirectionConfig = {
  redirectionEnabled: boolean;
  redirectEducation?: boolean; // When false, education routes stay in UPP instead of redirecting to legacy PP
  url: string;
};

type PreBillConfig = {
  stripeKey: string;
  postBillStripeKey: string;
  contactUsChatbot: string;
};

export type MaintenanceConfig = {
  enabled: boolean;
  untilDateTime: string;
};

type OtpVerificationConfig = {
  enabled: boolean;
};

export type PersonaToolingConfig = {
  enabled: boolean;
};

export type RecaptchaConfig = typeof defaultRecaptchaConfig;

export type Config = typeof defaultConfig & { okta: OktaConfig } & {
  maintenance?: MaintenanceConfig;
} & {
  otpVerification?: OtpVerificationConfig;
} & {
  personaTooling?: PersonaToolingConfig;
} & {
  heap?: HeapConfig;
} & {
  clientConfig?: ClientConfig;
} & {
  nevaConfig: NevaConfig;
} & {
  pippaConfig?: PippaConfig;
} & {
  ddRum: DDRumConfig;
} & {
  patientPortal: RedirectionConfig;
} & {
  recaptcha: RecaptchaConfig;
} & {
  preBill: PreBillConfig;
};

export const Context = createContext<ConfigService<Config>>(
  {} as ConfigService<Config>
);

const CONFIG_URL = "/config";
// Retry the config fetch before falling back to defaults: a transient failure
// strands the client on bundled defaults (reCAPTCHA off / empty keys).
const CONFIG_FETCH_MAX_ATTEMPTS = 3;
// Zero backoff under test so it doesn't slow suites with real timers.
const CONFIG_FETCH_RETRY_BASE_MS = process.env.NODE_ENV === "test" ? 0 : 300;

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const normalizeLogLevel = (level: string | undefined): LogLevel =>
  level === "info" || level === "warn" || level === "error" ? level : "debug";

const configureLogger = (config: Config): void => {
  configureClientLogger({
    enabled: config.clientLogs.enabled,
    level: normalizeLogLevel(config.clientLogs.level),
  });
};

// Returns the parsed /config body. Typed loosely to mirror the previous
// `response.json()` (Promise<any>) so the existing runtimeConfig mapping below
// keeps compiling unchanged.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fetchRuntimeConfig = async (): Promise<any> => {
  let lastError: unknown = new Error(
    "Runtime config fetch failed - no error details captured"
  );
  for (let attempt = 1; attempt <= CONFIG_FETCH_MAX_ATTEMPTS; attempt += 1) {
    try {
      const response = await fetch(CONFIG_URL);
      // A 4xx/5xx still resolves the fetch promise — treat it as a retryable
      // failure so we don't try to JSON-parse an error page into config.
      if (!response.ok) {
        throw new Error(`/config responded with status ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      lastError = error;

      console.warn(
        `Runtime config fetch attempt ${attempt}/${CONFIG_FETCH_MAX_ATTEMPTS} failed`,
        error
      );
      if (attempt < CONFIG_FETCH_MAX_ATTEMPTS) {
        // Linear backoff: 300ms, then 600ms. Worst case ~900ms before we fall
        // back to defaults, which is acceptable for app boot.
        await delay(CONFIG_FETCH_RETRY_BASE_MS * attempt);
      }
    }
  }
  throw lastError;
};

interface ConfigProviderProps {
  children: React.ReactNode;
  config: Config;
}

const ConfigProvider: FC<ConfigProviderProps> = ({ children, config }) => {
  const configResourceController = useResource<Config>({
    load: () =>
      fetchRuntimeConfig()
        .then((runtimeConfig) => {
          const configs: Config = {
            ...config,
            ...runtimeConfig.clientConfig,
            okta: { ...defaultOktaConfig, ...runtimeConfig.okta },
            heap: { ...defaultHeapConfig, ...runtimeConfig.heap },
            maintenance: {
              ...defaultMaintenanceConfig,
              ...runtimeConfig.maintenance,
            },
            otpVerification: runtimeConfig.otpVerification,
            personaTooling: runtimeConfig.personaTooling,
            patientPortal: runtimeConfig.patientPortal,
            preBill: {
              ...defaultConfig.preBill,
              ...runtimeConfig.clientConfig.preBill,
            },
            buildNumber: runtimeConfig.buildNumber,
            recaptcha: runtimeConfig.recaptcha,
            nevaConfig: {
              ...config.nevaConfig,
              ...runtimeConfig.clientConfig.nevaConfig,
            },
            pippaConfig: {
              ...config.pippaConfig,
              ...runtimeConfig.clientConfig.pippaConfig,
            },
            wistia: {
              ...config.wistia,
              ...runtimeConfig.clientConfig.wistia,
            },
            clientLogs:
              runtimeConfig.clientConfig.clientLogs ?? config.clientLogs,
          };
          const heapId = configs.heap?.app_id;
          if (heapId) {
            heap.load(heapId);
            wrapHeapSafely();
          }
          configs.ddRum && initializeDatadogRum(configs.ddRum);
          configureLogger(configs);
          return configs;
        })
        .catch((error) => {
          const configs = {
            ...defaultConfig,
            okta: defaultOktaConfig,
            heap: defaultHeapConfig,
            maintenance: defaultMaintenanceConfig,
            recaptcha: defaultRecaptchaConfig,
          };
          // Bundled defaults enable logging for local dev, so a production
          // bundle on the fallback path has to stay quiet.
          const fallbackLogs: ClientLogsConfig =
            process.env.NODE_ENV === "production"
              ? { enabled: false, level: "error" }
              : {
                  enabled: configs.clientLogs.enabled,
                  level: normalizeLogLevel(configs.clientLogs.level),
                };
          configureClientLogger(fallbackLogs);
          const heapId = configs.heap?.app_id;
          if (heapId) {
            heap.load(heapId);
            wrapHeapSafely();
          }
          configs.ddRum && initializeDatadogRum(configs.ddRum);
          // Report the fallback so it's queryable instead of silent. RUM here
          // uses default (non-prod) creds, so prod fallbacks land in non-prod.

          console.error(
            "Runtime config load failed after retries — using bundled defaults",
            error
          );
          // Best-effort only: telemetry must never stop the app from booting on
          // defaults (e.g. if RUM isn't initialized here).
          try {
            datadogRum.addError(
              error instanceof Error
                ? error
                : new Error(
                    "Runtime config load failed — using bundled defaults"
                  ),
              { source: "config_load_fallback", usingBundledDefaults: true }
            );
          } catch {
            // swallow — fallback config is already being returned below
          }
          return configs;
        }),
  });

  useEffect(() => {
    configResourceController.load();
  }, []);

  const portalConfig = configResourceController.getResource();

  const createConfigService = useCallback(() => {
    if (!portalConfig) {
      throw new Error("Can't load runtime configuration");
    }

    return new ConfigService(portalConfig);
  }, [portalConfig]);

  return (
    <LoadingContext
      isLoading={!portalConfig || configResourceController.isLoading()}
    >
      {portalConfig && (
        <Context.Provider value={createConfigService()}>
          {children}
        </Context.Provider>
      )}
    </LoadingContext>
  );
};

export default ConfigProvider;
